/**
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 * Filling a field from a list: the lookup editor sheet, in two panes.
 *
 * The left pane is the setup, as three numbered steps and the matching rules:
 * 1 Read from (the field whose reading finds the row), 2 Look it up in (the list,
 * the column it is matched against and the list itself with the found row lit), and
 * 3 Fill from the row (one row per field, with what to do when no row matches).
 *
 * The right pane is what happens to a reading, and it never scrolls away: the box to
 * type one in with its mark (✓ found, ✕ nothing, ! more than one row), the result
 * card, what it fills, the measurement over the field's last readings, and Save at
 * its foot. It exists because the result used to land a long scroll below the box it
 * answered, with Save below that.
 *
 * Below about 860 pixels of sheet the panes stack, with the reading and its result on
 * top, so the answer still sits right under the box it answers. In two panes the left
 * pane is the one region that scrolls; stacked, the sheet scrolls and nothing in it
 * does — one scroll context either way.
 *
 * Nothing here decides a match or measures one. `field-can-match-column` answers
 * whether the pairing can ever work, before anything is tested; `test-lookup`
 * answers the trial reading; `measure-recent-readings` answers what each rung
 * fills over this field's own last readings. A local copy of any of the three
 * would be a number that drifts from the one the documents actually get.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Database,
  Eraser,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Inbox,
  LoaderCircle,
  Lock,
  Pencil,
  Plus,
  RotateCw,
  SlidersHorizontal,
  SpellCheck,
  Table2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import type {
  DataTableCatalog,
  DataTableColumnProfile,
  DataTablePreview,
  DataTableSummary,
  FieldLookupAssignment,
  LookupCostReport,
  LookupFitVerdict,
  LookupMatch,
  LookupReturn,
  LookupRungCost,
} from '../shared/desktop-api';
import { fieldLookup, fieldLookups, usesListSpelling } from '../shared/lookup-rules';
import {
  diagnoseNoMatch,
  normalizeValueKind,
  profileColumn,
  STRICT_SWITCH,
  VALUE_KIND_CODE,
  VALUE_KIND_NAME,
} from './lookup-analysis';
import { OperatorErrorText } from './TechnicalDetails';
import { TooltipButton } from './TooltipButton';
import {
  fieldPatchesForSave,
  fromRuleDefinition,
  READINGS_COUNTED,
  spellingSwitches,
  toRuleDefinition,
  whatIsMissing,
  withSwitchesOf,
  type JourneyDraft,
} from './lookup-journey';
import './lookup-journey-workspace.css';
import { WorkspaceDialog } from './WorkspaceDialog';
import type { PublishNotice } from './WorkspaceNotice';
import { closePermissionFrozen, registerCloseBlocker } from './close-permission';
import { hostHas } from '../shared/host-capabilities';

/** The part of a job field this screen reads and writes. */
export type JourneyField = {
  id: string;
  name: string;
  kind: string;
  lookups?: FieldLookupAssignment[] | null;
};

/**
 * The only thing a journey writes back onto a field. Narrower than
 * `Partial<JourneyField>` on purpose: a patch that could carry `kind` or `name` is
 * a patch that could rename a field as a side effect of saving a rule.
 */
export type FieldPatch = {
  fieldId: string;
  patch: Pick<JourneyField, 'lookups'>;
};

/**
 * Whether another field's reading fills this field through a lookup other than `exceptRuleId`.
 *
 * Such a field cannot find a row itself: the engine refuses a lookup whose reading is
 * filled by a different field's lookup. A field matched on its own reading still can.
 */
const filledFromAnotherField = (field: JourneyField, exceptRuleId?: string) =>
  fieldLookups(field).some(
    (assignment) =>
      assignment.ruleId !== exceptRuleId &&
      !!assignment.sourceFieldId &&
      assignment.sourceFieldId !== field.id,
  );

/** The parent routes sidebar, Done and outer close through this same guard. */
export type LookupJourneyNavigationGuard = {
  requestLeave: (leave: () => void) => void;
  hasUnsavedChanges: boolean;
  busy: boolean;
};

/** Rows read once per list: enough to draw it and to profile its columns. */
const SAMPLE_ROWS = 500;
/** How long a keystroke waits before the engine is asked anything. */
const SETTLE_MS = 350;

const percent = (ratio: number) => `${Math.round(ratio * 100)}%`;
const plural = (count: number, word: string) =>
  `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;
const reasonWhy = (reason: unknown, fallback: string) =>
  reason instanceof Error ? reason.message : fallback;
/** Leaving refused while a change saves; every other footer error is a change that was not saved. */
const WAIT_TO_LEAVE = 'Wait for this change to finish saving before leaving.';
/** The screen's one word for each kind of value: the chip says Aa Words or # Codes. */
const kindWord = (valueKind: string) =>
  normalizeValueKind(valueKind) === VALUE_KIND_CODE ? 'codes' : 'words';
/** The list rows drawn under step 2 when no reading has landed further down. */
const PREVIEW_ROWS = 6;
/** Columns drawn in the preview; the rest are counted under it rather than squeezed in. */
const PREVIEW_COLUMNS = 5;

// -----------------------------------------------------------------------------
// The list behind the journey
// -----------------------------------------------------------------------------

type ListSample = {
  columnNames: string[];
  rows: string[][];
  profiles: DataTableColumnProfile[];
  totalRowCount: number;
  loading: boolean;
  error: string;
  /** Reads the same rows again after a failure; nothing else changes. */
  retry: () => void;
};

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function useListSample(jobId: string, table: DataTableSummary | undefined): ListSample {
  const tableId = table?.id ?? '';
  const [loaded, setLoaded] = useState<{ tableId: string; preview: DataTablePreview } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!tableId || !desktop) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    desktop
      .previewDataTable({ jobId, dataTableId: tableId, limit: SAMPLE_ROWS, offset: 0 })
      .then((preview) => {
        if (!cancelled) setLoaded({ tableId, preview });
      })
      .catch((reason) => {
        if (!cancelled) setError(reasonWhy(reason, 'The rows of that list could not be read.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, tableId, attempt]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const preview = loaded?.tableId === tableId ? loaded.preview : null;
  const profiles = useMemo(
    () =>
      preview
        ? preview.columnNames.map((column, index) =>
            profileColumn(
              column,
              preview.rows.map((row) => row[index] ?? ''),
            ),
          )
        : [],
    [preview],
  );
  return {
    columnNames: preview?.columnNames ?? table?.columnNames ?? [],
    rows: preview?.rows ?? [],
    profiles,
    totalRowCount: preview?.totalRowCount ?? table?.rowCount ?? 0,
    loading,
    error,
    retry,
  };
}

/**
 * What this field has actually been reading, newest first.
 *
 * Two uses, both of which need the values themselves rather than a count: the
 * reading drawn on the page starts as this field's most recent one, and the
 * question of whether this field's values could ever be values in that column is
 * answered better with examples than with a declared kind.
 */
function useFieldReadings(jobId: string, field: JourneyField | null): string[] {
  const fieldId = field?.id ?? '';
  const fieldName = field?.name ?? '';
  const identity = JSON.stringify([jobId, fieldId, fieldName]);
  const [loaded, setLoaded] = useState<{ identity: string; values: string[] } | null>(null);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !fieldId) return undefined;
    let cancelled = false;
    desktop
      .getLatestBatch(jobId)
      .then((batch) => {
        if (cancelled) return;
        const values: string[] = [];
        for (const document of batch?.documents ?? []) {
          for (const read of document.fields) {
            const isThisField = read.fieldId ? read.fieldId === fieldId : read.name === fieldName;
            if (isThisField && read.value.trim().length > 0) values.push(read.value.trim());
          }
        }
        setLoaded({ identity, values: values.slice(-READINGS_COUNTED).reverse() });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ identity, values: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, fieldId, fieldName, identity]);
  // A changed field must not borrow the previous field's examples while its
  // own read is pending. Effect cleanup alone only rejects the late response.
  return loaded?.identity === identity ? loaded.values : [];
}

// -----------------------------------------------------------------------------
// The screen
// -----------------------------------------------------------------------------

export function LookupJourney({
  jobId,
  fields,
  targetFieldId,
  onClose,
  onToast,
  onFieldsPatch,
  onRulesChanged,
  embedded = false,
  jobName,
  documentTypeName,
  onOpenLists,
  onNavigationGuardChange,
  preferredDataTableId,
}: {
  jobId: string;
  fields: JourneyField[];
  targetFieldId: string;
  onClose: () => void;
  onToast: PublishNotice;
  onFieldsPatch: (patches: FieldPatch[]) => void;
  onRulesChanged?: () => void;
  /** The parent keeps the job header and navigation visible around this editor. */
  embedded?: boolean;
  jobName?: string;
  documentTypeName?: string;
  onOpenLists?: () => void;
  onNavigationGuardChange?: (guard: LookupJourneyNavigationGuard | null) => void;
  /** The list a new lookup starts on when the field is not wired to one yet. */
  preferredDataTableId?: string;
}) {
  const ownerIdentity = JSON.stringify([jobId, targetFieldId]);
  const owner = useRef({ identity: ownerIdentity, generation: 0, mounted: false });
  if (owner.current.identity !== ownerIdentity) {
    owner.current.identity = ownerIdentity;
    owner.current.generation += 1;
  }
  const callbacks = useRef({ onFieldsPatch, onToast, onRulesChanged });
  callbacks.current = { onFieldsPatch, onToast, onRulesChanged };
  useEffect(() => {
    owner.current.mounted = true;
    owner.current.generation += 1;
    return () => {
      owner.current.mounted = false;
      owner.current.generation += 1;
    };
  }, []);
  const owns = (generation: number) =>
    owner.current.mounted && owner.current.generation === generation;
  const catalogRequest = useRef(0);
  const [catalog, setCatalog] = useState<DataTableCatalog | null>(null);
  /** The lists could not be read. Said at step 2, where the lists are, with Retry beside it. */
  const [catalogError, setCatalogError] = useState('');
  /** Save and leave refusals, said just above Save. */
  const [error, setError] = useState('');
  /** An import refusal, said beside the import that failed. */
  const [importError, setImportError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Which of the two writes `busy` is: the Save button says Saving…, the list picker Importing…. */
  const [saving, setSaving] = useState(false);
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const matchColumnRef = useRef<HTMLSelectElement>(null);
  const renameRef = useRef<HTMLButtonElement>(null);
  const [openMenu, setOpenMenu] = useState('');
  const [typedProbe, setProbe] = useState('');
  const [probeTouched, setProbeTouched] = useState(false);
  const [verdict, setVerdict] = useState<
    | { signature: string; status: 'pending' }
    | { signature: string; status: 'error'; error: string }
    | { signature: string; status: 'ready'; match: LookupMatch; row: string[] | null }
    | null
  >(null);
  const [trialAttempt, setTrialAttempt] = useState(0);
  const [cost, setCost] = useState<{
    signature: string;
    report: LookupCostReport | null;
    error: string;
  } | null>(null);
  const [costAttempt, setCostAttempt] = useState(0);
  const [fit, setFit] = useState<
    | { signature: string; status: 'pending' }
    | { signature: string; status: 'error'; error: string }
    | { signature: string; status: 'ready'; verdict: LookupFitVerdict }
    | null
  >(null);
  const [fitAttempt, setFitAttempt] = useState(0);
  const [draft, setDraft] = useState<JourneyDraft | null>(null);
  const [savedDraft, setSavedDraft] = useState('');
  /** Opened from a list for a field not yet on it: saving moves the field onto this list. */
  const [switchingField, setSwitchingField] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<{ leave: () => void } | null>(null);
  const writing = useRef(false);
  const hasUnsavedChanges =
    (draft !== null && savedDraft !== '' && JSON.stringify(draft) !== savedDraft) ||
    (importing && importName.trim().length > 0);
  const navigationState = useRef({ dirty: hasUnsavedChanges });
  navigationState.current.dirty = hasUnsavedChanges;
  const requestLeave = useCallback((leave: () => void) => {
    if (writing.current) {
      setError(WAIT_TO_LEAVE);
      return;
    }
    if (closePermissionFrozen()) return;
    if (navigationState.current.dirty) {
      setPendingLeave({ leave });
      return;
    }
    leave();
  }, []);
  useEffect(() => {
    onNavigationGuardChange?.({ requestLeave, hasUnsavedChanges, busy });
  }, [onNavigationGuardChange, requestLeave, hasUnsavedChanges, busy]);
  useEffect(() => () => onNavigationGuardChange?.(null), [onNavigationGuardChange]);
  useEffect(
    () =>
      registerCloseBlocker(() =>
        writing.current ? 'saving' : navigationState.current.dirty ? 'drafts' : 'idle',
      ),
    [],
  );

  const target = fields.find((field) => field.id === targetFieldId) ?? null;
  const tables = useMemo(() => catalog?.tables ?? [], [catalog]);
  const rules = useMemo(() => catalog?.rules ?? [], [catalog]);

  const refresh = useCallback(async () => {
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    const generation = owner.current.generation;
    const request = ++catalogRequest.current;
    try {
      const next = await desktop.listDataTables(jobId);
      if (owns(generation) && request === catalogRequest.current) {
        setCatalog(next);
        setCatalogError('');
      }
    } catch (reason) {
      if (owns(generation) && request === catalogRequest.current)
        setCatalogError(reasonWhy(reason, 'The lists for this job could not be read.'));
    }
  }, [jobId]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // One scroll context, guaranteed rather than hoped for: the surface behind this
  // one is a full page with its own bar, and two bars is the operator working out
  // which one the wheel is talking to.
  useEffect(() => {
    if (embedded) return;
    const held = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = held;
    };
  }, [embedded]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || embedded || pendingLeave || event.defaultPrevented) return;
      if (openMenu) {
        setOpenMenu('');
        return;
      }
      requestLeave(onClose);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, openMenu, embedded, requestLeave, pendingLeave]);

  // The draft is seeded from however this field is already wired, and otherwise
  // from the first list on the workstation — a journey that opens on nothing
  // chosen is a journey that looks broken.
  useEffect(() => {
    if (draft || tables.length === 0 || !target) return;
    // Filled from a row, or held to the column it was read from: one stored rule
    // and one journey, so both open the one that is already there. Opened for a
    // list ("Use this list for a field", or a field under a list's Used by), it is
    // the field's lookup on that list, and a field that only uses other lists
    // starts a new lookup on this one rather than opening a different list's.
    const ruleOf = (assignment: FieldLookupAssignment | null | undefined) =>
      rules.find((rule) => rule.id === assignment?.ruleId);
    const wired = preferredDataTableId
      ? fieldLookups(target).find(
          (assignment) => ruleOf(assignment)?.dataTableId === preferredDataTableId,
        )
      : fieldLookup(target);
    const existing = ruleOf(wired);
    const table =
      tables.find((candidate) => candidate.id === existing?.dataTableId) ??
      tables.find((candidate) => candidate.id === preferredDataTableId) ??
      tables[0];
    const seed = (value: JourneyDraft) => {
      setDraft(value);
      setSavedDraft(JSON.stringify(value));
    };
    if (existing && existing.returns && existing.returns.length > 0) {
      seed({
        ...fromRuleDefinition(existing),
        // No source is the field's own reading.
        sourceFieldId: wired?.sourceFieldId || targetFieldId,
        onMiss: wired?.noMatchBehaviour ?? 'review',
      });
      return;
    }
    // Picked under a list's "Use this list for a field" while it uses another list,
    // the field is being moved onto this one, not given a second lookup.
    setSwitchingField(Boolean(preferredDataTableId) && !wired);
    const columns = table.columnNames;
    // A new draft starts from matching names when the list offers them — a
    // "Vendor number" field fills from a "VENDOR_NUMBER" column, found by the
    // field whose name matches another column — and from position otherwise.
    // The fit check below still judges the pairing; this only saves the picking.
    const sameName = (left: string, right: string) =>
      left.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '') ===
      right.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    const returnColumn =
      columns.find((column) => sameName(column, target.name)) ?? columns[1] ?? columns[0] ?? '';
    const readable = fields.filter(
      (field) =>
        field.id !== targetFieldId &&
        field.kind !== 'line-items' &&
        field.kind !== 'document-type' &&
        !filledFromAnotherField(field),
    );
    const pair = readable
      .map((field) => ({
        field,
        column: columns.find((column) => column !== returnColumn && sameName(column, field.name)),
      }))
      .find((entry) => entry.column);
    seed({
      ruleId: '',
      name: `${target.name} from a list`,
      dataTableId: table.id,
      sourceFieldId: pair?.field.id ?? readable[0]?.id ?? null,
      findColumn:
        pair?.column ?? columns.find((column) => column !== returnColumn) ?? columns[0] ?? '',
      returns: [{ column: returnColumn, fieldName: target.name, fieldId: target.id }],
      ladder: { spelling: {}, forgiving: false, minimumScore: 0.92, runnerUpMargin: 0.05 },
      onMiss: fieldLookup(target)?.noMatchBehaviour ?? 'review',
    });
  }, [draft, tables, rules, fields, target, targetFieldId, preferredDataTableId]);

  const table = tables.find((candidate) => candidate.id === draft?.dataTableId);
  const sample = useListSample(jobId, table);
  const sourceField = fields.find((field) => field.id === draft?.sourceFieldId) ?? null;
  const readings = useFieldReadings(jobId, sourceField);
  // An untouched trial follows only this source's examples; explicit typing is
  // operator-owned and remains available when another source is selected.
  const probe = probeTouched ? typedProbe : (readings[0] ?? '');

  const columns = sample.columnNames;
  const profileOf = useCallback(
    (column: string) =>
      sample.profiles.find((profile) => profile.column.toLowerCase() === column.toLowerCase()) ??
      null,
    [sample.profiles],
  );
  // Derived, never asked. What kind of value a column holds is arithmetic on the
  // column; asking it on screen was a question whose whole effect happened off it.
  // The engine's answer about the whole column wins over the profile of the rows
  // read here. Until either is in, the kind is not known and nothing is tried or
  // saved: a rule sent as "name" in the meantime is a different rule (close
  // spellings, not confused glyphs, and the name switches).
  const fitSignature =
    draft?.dataTableId && draft.findColumn && sourceField
      ? JSON.stringify([
          ownerIdentity,
          draft.dataTableId,
          draft.findColumn,
          sourceField.id,
          sourceField.name,
          readings,
        ])
      : '';
  const fitColumnKind =
    fit?.status === 'ready' && fit.signature === fitSignature
      ? (fit.verdict.columnValueKind ?? '')
      : '';
  const knownKind = fitColumnKind || profileOf(draft?.findColumn ?? '')?.proposedValueKind || '';
  const kind = normalizeValueKind(knownKind || VALUE_KIND_NAME);

  // A switch this kind has and the rule does not state is on, which is exactly
  // what the engine does. Only missing ids are added: nothing held is removed or
  // changed, so opening a journey never silently changes what a rule matches.
  useEffect(() => {
    if (!draft) return;
    const settled = withSwitchesOf(draft.ladder.spelling, kind);
    if (settled !== draft.ladder.spelling) {
      const normalized = { ...draft, ladder: { ...draft.ladder, spelling: settled } };
      // An added id says what its absence already meant, so it is not an operator
      // edit: a clean baseline stays clean, and a pending change stays pending.
      if (JSON.stringify(draft) === savedDraft) setSavedDraft(JSON.stringify(normalized));
      setDraft(normalized);
    }
  }, [draft, kind, savedDraft]);

  // A source field deleted since the lookup was made is as missing as none: no fit check runs
  // without it, so Save said "Checking the list…" forever (chunk 28, R4-3).
  const missing = draft
    ? whatIsMissing(draft.sourceFieldId && !sourceField ? { ...draft, sourceFieldId: null } : draft)
    : 'Choose a list.';
  const rule =
    draft && table && missing === null && knownKind ? toRuleDefinition(draft, jobId, kind) : null;
  // The profile's kind may drive the trial and the preview, never a save: it is read
  // off the rows read here, the engine's off the whole column, and a rule saved with
  // a kind the engine disagrees with is refused (or matches the wrong way). Only the
  // engine's answer is saved, so until it is in (or after it failed) Save waits.
  const ruleToSave = fitColumnKind ? rule : null;
  const signature = rule
    ? JSON.stringify([rule.dataTableId, rule.findColumn, rule.returns, rule.matchLadder])
    : '';
  const costSignature = JSON.stringify([jobId, sourceField?.id, sourceField?.name, signature]);

  // ---------------------------------------------------------------------------
  // Can this pairing ever work — asked of the two columns, before anything is run
  // ---------------------------------------------------------------------------
  const currentFitSignature = useRef(fitSignature);
  currentFitSignature.current = fitSignature;
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!draft?.dataTableId || !draft.findColumn || !sourceField) {
      setFit(null);
      return undefined;
    }
    let cancelled = false;
    const generation = owner.current.generation;
    const current = () =>
      !cancelled && owns(generation) && currentFitSignature.current === fitSignature;
    setFit({ signature: fitSignature, status: 'pending' });
    const timer = setTimeout(() => {
      void (async () => {
        try {
          if (!current()) return;
          if (!desktop?.fieldCanMatchColumn)
            throw new Error('The reading shape could not be checked.');
          const answer = await desktop.fieldCanMatchColumn({
            jobId,
            dataTableId: draft.dataTableId,
            column: draft.findColumn,
            sourceFieldName: sourceField.name,
            sourceValues: readings,
          });
          if (current()) setFit({ signature: fitSignature, status: 'ready', verdict: answer });
        } catch (reason) {
          if (current())
            setFit({
              signature: fitSignature,
              status: 'error',
              error:
                reasonWhy(reason, 'The reading shape could not be checked.').trim() ||
                'The reading shape could not be checked.',
            });
        }
      })();
    }, SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId, fitSignature, fitAttempt]);
  const shownFitRead = fit && fit.signature === fitSignature ? fit : null;
  const shownFit = shownFitRead?.status === 'ready' ? shownFitRead.verdict : null;
  const fitError = shownFitRead?.status === 'error' ? shownFitRead.error : '';
  // Both spellings are accepted because both are in the tree right now: the shared
  // type declares `same-shape` / `different-shape` and the engine answers `fits` /
  // `mismatch`, and nothing between them maps one to the other. Reading only the
  // declared spelling would leave the wire never stopping — the feature silently
  // absent — which is precisely the class of failure the shared payload module was
  // written to end. This accepts both rather than pretending the seam is settled;
  // it belongs in one vocabulary, and the report says so.
  const differentShape = (verdict: LookupFitVerdict) =>
    verdict.fit === 'different-shape' || String(verdict.fit) === 'mismatch';
  const shapeKnown = (verdict: LookupFitVerdict) =>
    verdict.fit !== 'unknown' && verdict.readingCount > 0;
  const mismatch = shownFit && differentShape(shownFit) ? shownFit : null;

  // ---------------------------------------------------------------------------
  // A typed sample may be tested independently of historical source-field fit.
  // It never overrides the source mismatch that blocks saving this connection.
  // ---------------------------------------------------------------------------
  const runnable = rule !== null && (mismatch === null || probeTouched) && probe.trim().length > 0;
  const trialSignature = JSON.stringify([
    ownerIdentity,
    sourceField?.id,
    sourceField?.name,
    signature,
    rule?.valueKind,
    probe,
  ]);
  const currentTrialSignature = useRef(trialSignature);
  currentTrialSignature.current = trialSignature;
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!rule || !runnable) {
      setVerdict(null);
      return undefined;
    }
    let cancelled = false;
    const generation = owner.current.generation;
    const current = () =>
      !cancelled && owns(generation) && currentTrialSignature.current === trialSignature;
    setVerdict({ signature: trialSignature, status: 'pending' });
    const timer = setTimeout(() => {
      void (async () => {
        try {
          if (!current()) return;
          if (!desktop?.testLookup)
            throw new Error('That reading could not be run through the list.');
          const match = await desktop.testLookup({ jobId, value: probe, rule });
          if (!current()) return;
          let row: string[] | null = null;
          if (match.rowNumber && match.rowNumber > 0) {
            const read = await desktop.previewDataTable({
              jobId,
              dataTableId: rule.dataTableId,
              limit: 1,
              offset: match.rowNumber - 1,
            });
            row = read.rows[0] ?? null;
          }
          if (current()) setVerdict({ signature: trialSignature, status: 'ready', match, row });
        } catch (reason) {
          if (current())
            setVerdict({
              signature: trialSignature,
              status: 'error',
              error:
                reasonWhy(reason, 'That reading could not be run through the list.').trim() ||
                'That reading could not be run through the list.',
            });
        }
      })();
    }, SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId, trialSignature, runnable, trialAttempt]);

  // ---------------------------------------------------------------------------
  // What each rung costs, over this field's own last readings. No Measure button:
  // a measurement an operator has to ask for is one that is out of date on screen,
  // and the operator could not tell it apart from the test box anyway.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !rule || !sourceField) {
      setCost(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      desktop
        .measureRecentReadings({
          jobId,
          sourceFieldName: sourceField.name,
          rule,
          readingCount: READINGS_COUNTED,
        })
        .then((report) => {
          if (!cancelled) setCost({ signature: costSignature, report, error: '' });
        })
        .catch((reason) => {
          if (!cancelled)
            setCost({
              signature: costSignature,
              report: null,
              error: reasonWhy(reason, 'The recent readings could not be measured.'),
            });
        });
    }, SETTLE_MS * 2);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId, costSignature, costAttempt]);
  const measured = cost && cost.signature === costSignature ? cost.report : null;
  const costError = cost && cost.signature === costSignature ? cost.error : '';
  const rungCost = (rung: LookupRungCost['rung']) =>
    measured?.rungs.find((entry) => entry.rung === rung) ?? null;
  /**
   * What stands where a rung's cost goes while there is not one.
   *
   * Three different silences, said differently. A rung stuck on "counting…"
   * forever reads as a screen that has hung, and a rung that reports nothing while
   * the sentence above it is still half-written reads as a broken measurement
   * rather than a sentence with a hole in it.
   */
  const rungNote =
    missing !== null
      ? 'counted once the sentence above is finished'
      : costError
        ? 'measurement unavailable'
        : measured !== null
          ? 'not measured for this rung'
          : 'counting…';
  const forgivingRung: LookupRungCost['rung'] =
    kind === VALUE_KIND_CODE ? 'character-confusion' : 'fuzzy';
  /**
   * The ladder said in one line, so the settings do not have to be on screen for
   * an operator to know what they are.
   *
   * Every rung open at once, each with its own switches and dials, is a wall of
   * forms on a screen somebody opens to answer one question. The rungs are tuned
   * rarely and read often, which is what a disclosure is for — but a disclosure
   * that hides the answer is worse than the wall, so the summary carries both
   * what is on and what it costs.
   */
  const ladderSay = [
    'exact spelling',
    draft?.ladder.forgiving
      ? kind === VALUE_KIND_CODE
        ? 'confusable glyphs'
        : `typos from ${percent(draft.ladder.minimumScore)} alike`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const patchDraft = (patch: Partial<JourneyDraft>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));
  const patchLadder = (patch: Partial<JourneyDraft['ladder']>) =>
    setDraft((current) =>
      current ? { ...current, ladder: { ...current.ladder, ...patch } } : current,
    );

  const importList = async () => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !importName.trim() || writing.current || closePermissionFrozen()) return;
    const generation = owner.current.generation;
    writing.current = true;
    setBusy(true);
    setImportError('');
    try {
      const result = await desktop.importDataTable({ jobId, name: importName.trim() });
      if (!owns(generation)) return;
      if (result) {
        onToast(`${result.table.name} · ${plural(result.table.rowCount, 'row')} read`);
        setImportName('');
        setImporting(false);
        await refresh();
        if (!owns(generation)) return;
        patchDraft({
          dataTableId: result.table.id,
          findColumn: result.table.columnNames[0] ?? '',
          returns: [
            {
              column: result.table.columnNames[1] ?? result.table.columnNames[0] ?? '',
              fieldName: target?.name ?? '',
              fieldId: targetFieldId,
            },
          ],
        });
      }
    } catch (reason) {
      if (owns(generation)) setImportError(reasonWhy(reason, 'That list could not be read.'));
    } finally {
      writing.current = false;
      if (owns(generation)) setBusy(false);
    }
  };

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const save = async () => {
    const desktop = window.capturePointDesktop;
    if (!desktop || !ruleToSave || !draft || writing.current || closePermissionFrozen()) return;
    const generation = owner.current.generation;
    writing.current = true;
    setBusy(true);
    setSaving(true);
    setError('');
    try {
      const saved = await desktop.saveLookupRule({ jobId, rule: ruleToSave });
      // The native rule may already be durable, but a closed/replaced journey
      // has no authority to replay its old setup snapshot over later edits.
      if (!owns(generation)) return;
      // The saved id goes onto whatever the draft is now: an edit made while this
      // save ran stays an unsaved change of THIS rule, not the start of a second one.
      setDraft((current) => (current ? { ...current, ruleId: saved.id } : current));
      setSavedDraft(JSON.stringify({ ...draft, ruleId: saved.id }));
      callbacks.current.onFieldsPatch(
        fieldPatchesForSave(fields, draft, saved.id, switchingField ? targetFieldId : null),
      );
      callbacks.current.onToast(`${saved.name} saved`);
      callbacks.current.onRulesChanged?.();
      await refresh();
    } catch (reason) {
      if (owns(generation)) setError(reasonWhy(reason, 'That could not be saved.'));
    } finally {
      writing.current = false;
      if (owns(generation)) {
        setBusy(false);
        setSaving(false);
      }
    }
  };

  // ---------------------------------------------------------------------------

  const shownTrialRead =
    runnable && verdict && verdict.signature === trialSignature ? verdict : null;
  const shownVerdict = shownTrialRead?.status === 'ready' ? shownTrialRead : null;
  const trialError = shownTrialRead?.status === 'error' ? shownTrialRead.error : '';
  const matchedRowNumber = shownVerdict?.match.rowNumber ?? null;
  /** A tie: the list answered with more than one row, and the engine refused to pick. */
  const tie = shownVerdict?.match.outcome === 'ambiguous' ? shownVerdict.match : null;
  const tiedRows = useMemo(
    () => (tie?.candidateRows ?? []).filter((row) => row.rowNumber > 0),
    [tie],
  );

  /**
   * The columns the preview draws: every one when the list is narrow enough, and
   * otherwise the matched and filled columns first, then the rest in file order up to
   * `PREVIEW_COLUMNS`. The others are counted under the table rather than squeezed in.
   */
  const shownColumns = useMemo(() => {
    const named = new Set<string>();
    if (draft?.findColumn) named.add(draft.findColumn);
    for (const entry of draft?.returns ?? []) if (entry.column) named.add(entry.column);
    if (columns.length <= PREVIEW_COLUMNS) return columns;
    const room = Math.max(0, PREVIEW_COLUMNS - named.size);
    const others = new Set(columns.filter((column) => !named.has(column)).slice(0, room));
    return columns.filter((column) => named.has(column) || others.has(column));
  }, [columns, draft]);

  /**
   * The first rows of the list, and — when they are not among them — the row the
   * reading landed on, or the rows it tied between.
   *
   * A directory has more than six rows and the match is hardly ever in the first
   * six, so drawing only the head left the found row lit nowhere. The row number
   * rides along because every verdict on this screen cites one, and a citation
   * nobody can check is decoration. A row past the rows read for the preview is drawn
   * from what the engine answered about it.
   */
  const shownRows = useMemo(() => {
    const valuesOf = (number: number): string[] | null => {
      const read = sample.rows[number - 1];
      if (read) return read;
      if (number === matchedRowNumber && shownVerdict?.row) return shownVerdict.row;
      const tied = tiedRows.find((row) => row.rowNumber === number);
      if (!tied) return null;
      return columns.map((column) =>
        column === draft?.findColumn
          ? (tied.matchedValue ?? '')
          : (tied.returns?.find((entry) => entry.column === column)?.value ?? ''),
      );
    };
    const lit = [...new Set([matchedRowNumber ?? 0, ...tiedRows.map((row) => row.rowNumber)])]
      .filter((number) => number > 0)
      .sort((a, b) => a - b);
    const headCount = Math.min(
      sample.rows.length,
      Math.max(2, PREVIEW_ROWS - lit.filter((number) => number > PREVIEW_ROWS).length),
    );
    const head = sample.rows
      .slice(0, headCount)
      .map((values, index) => ({ number: index + 1, values }));
    const extra = lit
      .filter((number) => number > headCount)
      .flatMap((number) => {
        const values = valuesOf(number);
        return values ? [{ number, values }] : [];
      });
    return [...head, ...extra];
  }, [sample.rows, matchedRowNumber, shownVerdict, tiedRows, columns, draft?.findColumn]);

  // What the reading did, as one state the box, the card and the list all draw.
  // "Impossible" never reached the list (the two sides hold different kinds of
  // value); "stopped" reached it and found no row, or more than one.
  const wireState =
    mismatch && !probeTouched
      ? 'impossible'
      : !runnable
        ? 'waiting'
        : trialError
          ? 'failed'
          : shownVerdict === null
            ? 'running'
            : shownVerdict.match.outcome === 'matched'
              ? 'through'
              : 'stopped';

  const fixes =
    draft && shownVerdict && shownVerdict.match.outcome === 'unmatched' && rule
      ? namedFix({
          draft,
          kind,
          verdict: shownVerdict.match,
          probe,
          columns,
          rows: sample.rows,
          totalRowCount: sample.totalRowCount,
          rule,
        })
      : null;

  const onMissSay =
    draft?.onMiss === 'keep'
      ? 'kept what was read'
      : draft?.onMiss === 'blank'
        ? 'left blank'
        : 'sent to review';
  const takeValue = (entry: LookupReturn): { text: string; filled: boolean } => {
    if (mismatch && !probeTouched) return { text: '-', filled: false };
    if (missing !== null) return { text: 'not set up yet', filled: false };
    if (trialError) return { text: '-', filled: false };
    if (!shownVerdict) return { text: probe.trim() ? '…' : '-', filled: false };
    // A tie goes to review whatever the field says to do when nothing matches: the
    // list did answer, twice, and only a person can say which row was meant.
    if (shownVerdict.match.outcome === 'ambiguous')
      return { text: 'sent to review', filled: false };
    if (shownVerdict.match.outcome !== 'matched' || !shownVerdict.row)
      return { text: onMissSay, filled: false };
    const value = (shownVerdict.row[columns.indexOf(entry.column)] ?? '').trim();
    return value.length > 0
      ? { text: value, filled: true }
      : { text: 'blank in this row', filled: false };
  };

  const spare = columns.filter(
    (column) =>
      column !== draft?.findColumn && !draft?.returns.some((entry) => entry.column === column),
  );
  // A field may be its own source. That is not a second feature with its own tab:
  // matching a field's own reading against a column and writing that column back
  // is saving the field with the list's spelling, and it is literally `find === fill`.
  const fieldsAvailable = fields.filter(
    (field) => field.kind !== 'line-items' && field.kind !== 'document-type',
  );
  const fieldOf = (entry: LookupReturn) =>
    fields.find((field) => field.id === entry.fieldId) ??
    fields.find((field) => field.name === entry.fieldName) ??
    null;
  /** The shared rule for "saved with the list's spelling", asked of the draft as it stands. */
  const listSpelling = (entry: LookupReturn) => {
    const field = fieldOf(entry);
    return (
      !!draft &&
      !!field &&
      entry.column === draft.findColumn &&
      usesListSpelling(
        field,
        { sourceFieldId: draft.sourceFieldId },
        { findColumn: draft.findColumn, returns: draft.returns },
      )
    );
  };
  const setReturnField = (index: number, fieldId: string) => {
    const chosen = fields.find((field) => field.id === fieldId);
    patchDraft({
      returns: (draft?.returns ?? []).map((entry, at) =>
        at === index
          ? { ...entry, fieldId: chosen?.id ?? null, fieldName: chosen?.name ?? '' }
          : entry,
      ),
    });
  };

  /** "Show me": opens the matching rules and puts focus on the one control the diagnosis names. */
  const showControl = (control: string) => {
    if (control === 'find-column') {
      matchColumnRef.current?.scrollIntoView({ block: 'center' });
      matchColumnRef.current?.focus();
      return;
    }
    setRulesOpen(true);
    window.requestAnimationFrame(() => {
      const shell = shellRef.current;
      const found =
        control === 'forgiving'
          ? shell?.querySelector<HTMLElement>('[data-rung="forgiving"] input[type="checkbox"]')
          : Object.values(STRICT_SWITCH).some((entry) => entry.id === control)
            ? shell?.querySelector<HTMLElement>(`[data-switch="${control}"] input[type="checkbox"]`)
            : control === 'minimum-score'
              ? shell?.querySelector<HTMLElement>('input[aria-label="How alike it has to be"]')
              : shell?.querySelector<HTMLElement>(
                  'input[aria-label="How far clear of the next best row"]',
                );
      found?.scrollIntoView({ block: 'center' });
      found?.focus();
    });
  };

  const measuredSay =
    measured && measured.measuredFrom === 'field-readings' && measured.readingCount > 0
      ? `fills ${measured.filled} of ${measured.readingCount} · ${measured.wrongRows} wrong`
      : '';
  const heldWord = draft?.onMiss === 'review' ? 'to review' : 'not filled';
  // A field another lookup reads to find its row can't be filled by this one: the engine refuses
  // chained lookups, and setup's Save failed far from here with this rule saved but unattached.
  const chainedTarget = !draft
    ? undefined
    : draft.returns
        .map(
          (entry) =>
            (entry.fieldId ? fields.find((field) => field.id === entry.fieldId) : undefined) ??
            fields.find((field) => field.name === entry.fieldName),
        )
        .find(
          (field) =>
            field &&
            fields.some((other) =>
              fieldLookups(other).some(
                (assignment) =>
                  assignment.ruleId !== draft.ruleId && assignment.sourceFieldId === field.id,
              ),
            ),
        );
  const lockReason = !draft
    ? null
    : mismatch
      ? 'Fix step 1 to save'
      : chainedTarget
        ? `${chainedTarget.name} is read by another lookup to find its row, so this lookup can't fill it`
        : (missing ??
          (!draft.name.trim()
            ? 'Name this lookup to save'
            : !knownKind
              ? 'Reading the list…'
              : !fitColumnKind
                ? fitError
                  ? 'Retry the check to save'
                  : 'Checking the list…'
                : null));
  const importRunning = busy && !saving && importing;
  const sourceChoices = fields.filter(
    (field) =>
      field.kind !== 'line-items' &&
      field.kind !== 'document-type' &&
      (field.id === targetFieldId || !filledFromAnotherField(field, draft?.ruleId)),
  );
  const notOffered = fields
    .filter((field) => !sourceChoices.includes(field))
    .map(
      (field) =>
        `${field.name} (${field.kind === 'line-items' ? 'a table' : field.kind === 'document-type' ? 'the document type' : 'another lookup fills it'})`,
    );
  const fitSay =
    shownFit && sourceField && draft
      ? `${sourceField.name} readings are ${kindWord(shownFit.sourceValueKind)}, like the ${draft.findColumn} column.`
      : '';
  const spareSay =
    spare.length === 0
      ? ''
      : spare.length <= 2
        ? `${spare.join(' and ')} ${spare.length === 1 ? 'is' : 'are'} free`
        : `${spare.length} more columns are free`;
  const tieCount = tie ? tiedRows.length || tie.candidates?.length || 0 : 0;
  /** How alike each tied row was, only where the engine measured it. */
  const tieAlike = (index: number) => {
    if (!tie) return '';
    if (tie.rung === 'fuzzy' && tiedRows.length === 2)
      return `${percent(index === 0 ? tie.score : tie.runnerUpScore)} alike`;
    if (tie.rung === 'exact') return 'exact';
    if (tie.rung === 'character-confusion') return 'look-alike letters';
    return '';
  };
  /** The engine's reason for a mismatch is the two kinds; said in the screen's words when it gives them. */
  const kindsDiffer =
    !!mismatch &&
    normalizeValueKind(mismatch.sourceValueKind) !== normalizeValueKind(mismatch.columnValueKind);
  const markState =
    wireState === 'through'
      ? 'ok'
      : wireState === 'running'
        ? 'busy'
        : wireState === 'failed'
          ? 'err'
          : wireState === 'stopped'
            ? tie
              ? 'tie'
              : 'no'
            : '';
  const matchedValue = (entry: LookupReturn) =>
    (shownVerdict?.row?.[columns.indexOf(entry.column)] ?? '').trim();

  return (
    <div
      ref={shellRef}
      className={`journey-shell journey-workspace${embedded ? ' journey-embedded' : ''}`}
      onMouseDown={() => setOpenMenu('')}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || pendingLeave) return;
        if (!openMenu) {
          if (embedded) {
            event.stopPropagation();
            event.preventDefault();
            requestLeave(onClose);
          }
          return;
        }
        event.stopPropagation();
        event.currentTarget
          .querySelector<HTMLButtonElement>(`[data-journey-picker="${openMenu}"]`)
          ?.focus();
        setOpenMenu('');
      }}
    >
      <header className="journey-top">
        {/* Inside Job setup this is a side sheet over the page it was opened from,
            so it names the field and closes; there is no second screen to go back from. */}
        <div className="journey-title">
          <span className="eyebrow">
            {embedded ? [documentTypeName, 'Lookup'].filter(Boolean).join(' · ') : 'Fill from list'}
          </span>
          <div className="journey-title-row">
            <h1>{target?.name ?? 'Choose a field'}</h1>
            {draft &&
              (editingName ? (
                <input
                  className="journey-savedas-input"
                  aria-label="Name for this saved lookup"
                  value={draft.name}
                  maxLength={160}
                  autoFocus
                  placeholder="e.g. Vendor number from the vendor list"
                  onChange={(event) => patchDraft({ name: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setEditingName(false);
                      window.requestAnimationFrame(() => renameRef.current?.focus());
                    }
                  }}
                />
              ) : (
                <span className="journey-savedas">
                  Saved as <b>{draft.name.trim() || 'no name yet'}</b>{' '}
                  <TooltipButton
                    ref={renameRef}
                    type="button"
                    className="journey-rename"
                    aria-label="Rename this lookup"
                    tooltip="Rename"
                    onClick={() => setEditingName(true)}
                  >
                    <Pencil size={14} />
                  </TooltipButton>
                </span>
              ))}
          </div>
        </div>
        <div className="journey-navigation">
          {onOpenLists && (
            <TooltipButton
              type="button"
              className="secondary-button small"
              tooltip="Lookup lists"
              disabled={busy}
              onClick={() => requestLeave(onOpenLists)}
            >
              <Database size={16} aria-hidden="true" />
              <span className="journey-nav-label">Lookup lists</span>
            </TooltipButton>
          )}
          <button
            className="icon-button journey-close"
            disabled={busy}
            onClick={() => requestLeave(onClose)}
            aria-label={embedded ? 'Close lookup' : 'Close this journey'}
          >
            <X size={20} />
          </button>
        </div>
      </header>
      {tables.length === 0 || !draft || !table ? (
        <div className="journey-panes journey-panes-empty">
          <div className="journey-steps">
            <section className="journey-step" aria-labelledby="journey-list-heading">
              <h2 id="journey-list-heading">
                <span className="journey-step-number">2</span>Look it up in
              </h2>
              {catalogError ? (
                <p className="journey-warn" role="alert" data-telemetry="error:lookup-load-failed">
                  <TriangleAlert size={16} />
                  <span>{catalogError}</span>
                  <button
                    type="button"
                    className="quiet-button small"
                    onClick={() => void refresh()}
                  >
                    <RotateCw size={14} />
                    Retry
                  </button>
                </p>
              ) : catalog && tables.length === 0 ? (
                <EmptyJourney
                  importing={importing}
                  busy={busy}
                  name={importName}
                  error={importError}
                  onName={setImportName}
                  onBegin={() => setImporting(true)}
                  onChoose={() => void importList()}
                />
              ) : (
                <p className="journey-loading" role="status">
                  <LoaderCircle className="spin" size={16} />
                  Reading the lists…
                </p>
              )}
            </section>
          </div>
          <section className="journey-trial journey-trial-off" aria-label="Try a reading">
            <span className="journey-trial-label">
              <FlaskConical size={16} />
              Try a reading
            </span>
            <div className="journey-probe-box off">
              <span>Add a list first</span>
            </div>
          </section>
          <footer className="journey-foot">
            {error && (
              <p
                className="journey-error"
                role="alert"
                data-telemetry={
                  error === WAIT_TO_LEAVE ? 'error:lookup-busy' : 'error:lookup-save-failed'
                }
              >
                <TriangleAlert size={16} />
                <span>{error}</span>
              </p>
            )}
            <div className="journey-foot-actions">
              <button
                className="secondary-button"
                onClick={() => requestLeave(onClose)}
                disabled={busy}
              >
                Close
              </button>
            </div>
          </footer>
        </div>
      ) : (
        <div className="journey-panes">
          <div className="journey-steps" aria-label="Set up the lookup">
            <section
              className="journey-step journey-source-zone"
              aria-labelledby="journey-source-heading"
            >
              <h2 id="journey-source-heading">
                <span className="journey-step-number">1</span>Read from
              </h2>
              <div className="journey-paper">
                <div className="journey-row">
                  <span className="journey-anchor">
                    <button
                      className={`journey-picker${mismatch ? ' warn' : ''}`}
                      data-journey-picker="source"
                      aria-expanded={openMenu === 'source'}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => setOpenMenu(openMenu === 'source' ? '' : 'source')}
                    >
                      <FileText size={16} aria-hidden="true" />
                      <span className="journey-picker-name">
                        {sourceField?.name ?? 'Choose the field'}
                      </span>
                      {sourceField?.id === targetFieldId && (
                        <small className="journey-tag">this field</small>
                      )}
                      {kindsDiffer && mismatch && <KindChip kind={mismatch.sourceValueKind} />}
                      <ChevronDown size={16} aria-hidden="true" />
                    </button>
                    {openMenu === 'source' && (
                      <div
                        className="journey-menu"
                        role="group"
                        aria-label="Document fields"
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        {sourceChoices.map((field) => (
                          <button
                            key={field.id}
                            className={field.id === draft.sourceFieldId ? 'pick' : ''}
                            onClick={(event) => {
                              patchDraft({ sourceFieldId: field.id });
                              event.currentTarget
                                .closest('.journey-anchor')
                                ?.querySelector<HTMLButtonElement>('.journey-picker')
                                ?.focus();
                              setOpenMenu('');
                            }}
                          >
                            <FileText size={16} aria-hidden="true" />
                            <span>
                              <b>{field.name}</b>
                              {field.id === targetFieldId && (
                                <>
                                  {' '}
                                  <small>this field</small>
                                </>
                              )}
                            </span>
                            {field.id === draft.sourceFieldId && (
                              <Check size={16} aria-hidden="true" />
                            )}
                          </button>
                        ))}
                        {notOffered.length > 0 && (
                          <p className="journey-menu-foot">Not offered: {notOffered.join(', ')}.</p>
                        )}
                      </div>
                    )}
                  </span>
                  {/* Words against words is the ordinary case, so it is a check and a
                      tooltip, not a sentence; only a pairing that can never match speaks. */}
                  <span
                    className="journey-readsas"
                    data-fit-state={!fitSignature ? 'idle' : (shownFitRead?.status ?? 'pending')}
                    data-reads-as={
                      shownFit && shapeKnown(shownFit)
                        ? normalizeValueKind(shownFit.sourceValueKind)
                        : 'unknown'
                    }
                  >
                    {fitError ? (
                      <>
                        <span role="alert" data-telemetry="error:lookup-check-failed">
                          Couldn't check this field's readings. {fitError}
                        </span>{' '}
                        <button
                          type="button"
                          className="quiet-button small"
                          onClick={() => {
                            setFit({ signature: fitSignature, status: 'pending' });
                            setFitAttempt((attempt) => attempt + 1);
                          }}
                        >
                          <RotateCw size={14} />
                          Retry check
                        </button>
                      </>
                    ) : !fitSignature || mismatch ? null : !shownFit ? (
                      <LoaderCircle
                        className="spin journey-fit-busy"
                        size={16}
                        role="img"
                        aria-label="Checking this field's readings"
                      />
                    ) : shapeKnown(shownFit) ? (
                      <TooltipButton
                        type="button"
                        className="journey-fit-ok"
                        tooltip="Fits this column"
                        aria-label={fitSay}
                        helpOnClick
                      >
                        <Check size={14} aria-hidden="true" />
                      </TooltipButton>
                    ) : null}
                  </span>
                </div>
                {mismatch && (
                  <p className="journey-warn journey-source-warning" role="status">
                    <TriangleAlert size={16} aria-hidden="true" />
                    {kindsDiffer ? (
                      <span>
                        <strong>
                          {sourceField?.name} holds {kindWord(mismatch.sourceValueKind)}
                          {mismatch.sourceExamples?.[0]
                            ? ` like ${mismatch.sourceExamples[0]}`
                            : ''}
                          .
                        </strong>{' '}
                        The {draft.findColumn} column holds {kindWord(mismatch.columnValueKind)}, so
                        they will never match. Pick the field that has {draft.findColumn} in it.
                      </span>
                    ) : (
                      <span>
                        <strong>{mismatch.headline}</strong> {mismatch.detail}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </section>
            <section
              className="journey-step journey-list-zone"
              aria-labelledby="journey-list-heading"
            >
              <h2 id="journey-list-heading">
                <span className="journey-step-number">2</span>Look it up in
              </h2>
              <div className="journey-row">
                <div className="journey-listchoose">
                  <span className="journey-anchor journey-listpick">
                    <button
                      className="journey-picker large"
                      data-journey-picker="list"
                      aria-expanded={openMenu === 'list'}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => setOpenMenu(openMenu === 'list' ? '' : 'list')}
                    >
                      {importRunning ? (
                        <>
                          <LoaderCircle className="spin" size={16} aria-hidden="true" />
                          <span className="journey-picker-name">
                            Importing {importName.trim()}…
                          </span>
                        </>
                      ) : (
                        <>
                          <FileSpreadsheet size={16} aria-hidden="true" />
                          <span className="journey-picker-name">{table.name}</span>{' '}
                          <small>
                            {plural(sample.totalRowCount, 'row')} ·{' '}
                            {plural(columns.length, 'column')}
                          </small>
                        </>
                      )}
                      <ChevronDown size={16} aria-hidden="true" />
                    </button>
                    {openMenu === 'list' && (
                      <div
                        className="journey-menu"
                        role="group"
                        aria-label="Available lists"
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        {tables.map((candidate) => (
                          <button
                            key={candidate.id}
                            className={candidate.id === draft.dataTableId ? 'pick' : ''}
                            onClick={(event) => {
                              patchDraft({
                                dataTableId: candidate.id,
                                findColumn: candidate.columnNames[0] ?? '',
                                returns: [
                                  {
                                    column:
                                      candidate.columnNames[1] ?? candidate.columnNames[0] ?? '',
                                    fieldName: target?.name ?? '',
                                    fieldId: targetFieldId,
                                  },
                                ],
                              });
                              event.currentTarget
                                .closest('.journey-anchor')
                                ?.querySelector<HTMLButtonElement>('.journey-picker')
                                ?.focus();
                              setOpenMenu('');
                            }}
                          >
                            <FileSpreadsheet size={16} aria-hidden="true" />
                            <span>
                              <b>{candidate.name}</b>{' '}
                              <small>
                                {plural(candidate.rowCount, 'row')} ·{' '}
                                {plural(candidate.columnNames.length, 'column')}
                              </small>
                            </span>
                            {candidate.id === draft.dataTableId && (
                              <Check size={16} aria-hidden="true" />
                            )}
                          </button>
                        ))}
                        <hr />
                        <button
                          className="journey-menu-import"
                          onClick={() => {
                            setImporting(true);
                            setOpenMenu('');
                          }}
                        >
                          <Upload size={16} aria-hidden="true" />
                          <span>
                            <b>Import another list…</b>
                          </span>
                        </button>
                      </div>
                    )}
                  </span>
                </div>
                <span className="journey-matchgroup">
                  <label className="journey-match-column">
                    <span className="journey-control-label">Match against</span>
                    <span className="journey-select">
                      <Table2 size={15} aria-hidden="true" />
                      <select
                        ref={matchColumnRef}
                        aria-label="Match against column"
                        value={draft.findColumn}
                        onChange={(event) => patchDraft({ findColumn: event.target.value })}
                      >
                        {columns.map((column) => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </span>
                  </label>
                  <KindChip kind={kind} />
                </span>
              </div>
              {importing && (
                <div className="journey-import">
                  <input
                    autoFocus
                    value={importName}
                    placeholder="e.g. Vendor list"
                    aria-label="New list name"
                    onChange={(event) => setImportName(event.target.value)}
                  />
                  <button
                    className="primary-button small"
                    disabled={busy || !importName.trim()}
                    onClick={() => void importList()}
                  >
                    {busy ? <LoaderCircle className="spin" size={15} /> : <Upload size={15} />}
                    Choose the file…
                  </button>
                  <button
                    className="quiet-button small"
                    onClick={() => {
                      setImporting(false);
                      setImportError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* A <div>: the message can carry a Technical details disclosure, which a <p> may not hold. */}
              {importError && (
                <div
                  className="journey-warn"
                  role="alert"
                  data-telemetry="error:lookup-import-failed"
                >
                  <TriangleAlert size={16} aria-hidden="true" />
                  <OperatorErrorText message={importError} />
                </div>
              )}
              {catalogError && (
                <p className="journey-warn" role="alert" data-telemetry="error:lookup-load-failed">
                  <TriangleAlert size={16} aria-hidden="true" />
                  <span>{catalogError}</span>
                  <button
                    type="button"
                    className="quiet-button small"
                    onClick={() => void refresh()}
                  >
                    <RotateCw size={14} />
                    Retry
                  </button>
                </p>
              )}
              <div className="journey-listbox">
                <table aria-label={`${table.name} preview`}>
                  <thead>
                    <tr>
                      <th className="rownum">#</th>
                      {shownColumns.map((column) => {
                        const fills = draft.returns.filter(
                          (entry) => entry.column === column && entry.fieldName,
                        );
                        return (
                          <th
                            key={column}
                            className={
                              column === draft.findColumn ? 'matched' : fills.length ? 'filled' : ''
                            }
                          >
                            <span className="journey-colname">{column}</span>
                            {column === draft.findColumn && (
                              <small className="journey-coltag match">Match</small>
                            )}
                            {fills.map((entry) => (
                              <small key={entry.fieldName} className="journey-coltag fill">
                                → {entry.fieldName}
                              </small>
                            ))}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.map((row) => {
                      const hit = matchedRowNumber === row.number,
                        tied = tiedRows.some((candidate) => candidate.rowNumber === row.number);
                      return (
                        <tr
                          key={row.number}
                          className={hit ? 'hit' : tied ? 'tie' : ''}
                          data-row={row.number}
                        >
                          <td className="rownum">
                            {hit ? (
                              <>
                                <Check size={14} aria-hidden="true" />
                                <span className="visually-hidden">
                                  {row.number.toLocaleString()}
                                </span>
                              </>
                            ) : (
                              row.number.toLocaleString()
                            )}
                          </td>
                          {shownColumns.map((column) => (
                            <td
                              key={column}
                              className={column === draft.findColumn ? 'matched' : ''}
                            >
                              {(row.values[columns.indexOf(column)] ?? '').trim() || '-'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {shownRows.length === 0 && (
                      <tr>
                        <td colSpan={shownColumns.length + 1}>
                          <em>
                            {sample.loading
                              ? 'Reading the list…'
                              : sample.error
                                ? 'Preview unavailable.'
                                : 'This list has no rows.'}
                          </em>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={shownColumns.length + 1}>
                        {plural(sample.totalRowCount, 'row')}
                        {shownRows.length > 0 ? ` · showing ${shownRows.length}` : ''}
                        {columns.length > shownColumns.length
                          ? ` · ${plural(columns.length - shownColumns.length, 'more column')} not shown`
                          : ''}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {sample.error && (
                <p className="journey-warn" role="alert" data-telemetry="error:lookup-load-failed">
                  <TriangleAlert size={16} aria-hidden="true" />
                  <span>{sample.error}</span>
                  <button type="button" className="quiet-button small" onClick={sample.retry}>
                    <RotateCw size={14} />
                    Retry
                  </button>
                </p>
              )}
            </section>
            <section
              className="journey-step journey-output-zone"
              aria-labelledby="journey-output-heading"
            >
              <h2 id="journey-output-heading">
                <span className="journey-step-number">3</span>Fill from the row
              </h2>
              <div className="journey-takes">
                {draft.returns.map((entry, index) => {
                  const shown = takeValue(entry);
                  const spelling = listSpelling(entry);
                  // Mapping slots are controlled inputs, so choosing a different
                  // column must not unmount the select that still owns focus.
                  return (
                    <div
                      className="journey-take"
                      key={index}
                      data-column={entry.column}
                      data-row={shown.filled && matchedRowNumber ? String(matchedRowNumber) : ''}
                    >
                      <div className="journey-return-mapping">
                        <label className="journey-takefield journey-select flat">
                          <FileText size={15} aria-hidden="true" />
                          <select
                            aria-label={`Which field ${entry.column} fills`}
                            value={
                              entry.fieldId ??
                              fields.find((field) => field.name === entry.fieldName)?.id ??
                              ''
                            }
                            onChange={(event) => setReturnField(index, event.target.value)}
                          >
                            <option value="">Choose a field</option>
                            {fieldsAvailable.map((field) => (
                              <option key={field.id} value={field.id}>
                                {field.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <ArrowLeft className="journey-take-arrow" size={15} aria-hidden="true" />
                        <label className="journey-takecolumn journey-select flat">
                          <Table2 size={15} aria-hidden="true" />
                          <select
                            aria-label={`List column for return ${index + 1}`}
                            value={entry.column}
                            onChange={(event) =>
                              patchDraft({
                                returns: draft.returns.map((item, at) =>
                                  at === index ? { ...item, column: event.target.value } : item,
                                ),
                              })
                            }
                          >
                            {columns.map((column) => (
                              <option key={column} value={column}>
                                {column}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <span className={`journey-takevalue ${shown.filled ? '' : 'none'}`}>
                        {shown.text}
                      </span>
                      <span className="journey-take-end">
                        {spelling && (
                          <span className="journey-spelling">
                            <SpellCheck size={15} aria-hidden="true" />
                            Uses the list's spelling
                          </span>
                        )}
                        {draft.returns.length > 1 && index > 0 && (
                          <TooltipButton
                            className="icon-button journey-takedrop"
                            aria-label={`Stop filling from ${entry.column}`}
                            tooltip="Stop filling this field"
                            onClick={() =>
                              patchDraft({ returns: draft.returns.filter((_, at) => at !== index) })
                            }
                          >
                            <X size={16} />
                          </TooltipButton>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              {spare.length > 0 && (
                <div className="journey-row journey-addrow">
                  <button
                    className="journey-addtake"
                    onClick={() => {
                      const free = fieldsAvailable.find(
                        (field) => !draft.returns.some((entry) => entry.fieldName === field.name),
                      );
                      patchDraft({
                        returns: [
                          ...draft.returns,
                          {
                            column: spare[0],
                            fieldName: free?.name ?? '',
                            fieldId: free?.id ?? null,
                          },
                        ],
                      });
                    }}
                  >
                    <Plus size={16} aria-hidden="true" />
                    Fill another field
                  </button>
                  <span className="journey-spare">{spareSay}</span>
                </div>
              )}
              <label className="journey-onmiss">
                <span className="journey-control-label">If no row matches</span>
                <span className="journey-select">
                  {draft.onMiss === 'blank' ? (
                    <Eraser size={15} aria-hidden="true" />
                  ) : draft.onMiss === 'keep' ? (
                    <FileText size={15} aria-hidden="true" />
                  ) : (
                    <Inbox size={15} aria-hidden="true" />
                  )}
                  <select
                    aria-label="If no row matches"
                    value={draft.onMiss}
                    onChange={(event) =>
                      patchDraft({ onMiss: event.target.value as JourneyDraft['onMiss'] })
                    }
                  >
                    <option value="review">Send it to review</option>
                    <option value="blank">Leave it blank</option>
                    <option value="keep">Keep what was read</option>
                  </select>
                </span>
              </label>
            </section>
            <details
              className="journey-matching-details"
              open={rulesOpen}
              onToggle={(event) => setRulesOpen(event.currentTarget.open)}
            >
              <summary>
                <SlidersHorizontal size={18} aria-hidden="true" />
                <strong>Matching rules</strong>
                <span className="journey-rules-say">
                  {ladderSay}
                  {measuredSay && (
                    <em className={measured && measured.wrongRows > 0 ? 'bad' : 'good'}>
                      {' '}
                      · {measuredSay}
                    </em>
                  )}
                </span>
                <ChevronDown size={17} aria-hidden="true" />
              </summary>
              <div className="journey-rungs">
                <Rung
                  id="spelling"
                  fixed
                  on
                  label="Spelled the same way"
                  detail={
                    kind === VALUE_KIND_CODE
                      ? 'Two readings count as the same code once these are ignored.'
                      : 'Two readings count as the same name once these are ignored.'
                  }
                  cost={rungCost('exact')}
                  note={rungNote}
                >
                  <div className="journey-ticks">
                    {spellingSwitches(kind).map((entry) => (
                      <label
                        key={entry.id}
                        className={`journey-tick${entry.fixed ? ' fixed' : ''}`}
                        data-switch={entry.id}
                      >
                        <input
                          type="checkbox"
                          checked={entry.fixed || draft.ladder.spelling[entry.id] !== false}
                          disabled={entry.fixed}
                          onChange={(event) =>
                            patchLadder({
                              spelling: {
                                ...draft.ladder.spelling,
                                [entry.id]: event.target.checked,
                              },
                            })
                          }
                        />
                        <span>
                          <b>{entry.label}</b>
                          <small>{entry.example}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </Rung>
                <Rung
                  id="forgiving"
                  on={draft.ladder.forgiving}
                  costly={(rungCost(forgivingRung)?.wrongRows ?? 0) > 0}
                  label={
                    kind === VALUE_KIND_CODE
                      ? 'Allowing for glyphs Cortexa confuses'
                      : 'Close enough to be a typo'
                  }
                  detail={
                    kind === VALUE_KIND_CODE
                      ? 'A closed set - 0/O, 1/I/l, 5/S, 8/B, 2/Z, 6/G, rn/m - and only when exactly one row survives it.'
                      : `At least ${percent(draft.ladder.minimumScore)} alike and ${percent(draft.ladder.runnerUpMargin)} clear of the next best row.`
                  }
                  cost={rungCost(forgivingRung)}
                  note={rungNote}
                  onToggle={(next) => patchLadder({ forgiving: next })}
                >
                  {kind === VALUE_KIND_NAME ? (
                    <>
                      {/* The number sits against the slider, where the eye already is. */}
                      <div className="journey-dials">
                        <div className="journey-dial">
                          <span className="journey-diallab">How alike it has to be</span>
                          <div className="journey-dialset">
                            <input
                              type="range"
                              min={80}
                              max={99}
                              step={1}
                              aria-label="How alike it has to be"
                              value={Math.round(draft.ladder.minimumScore * 100)}
                              onChange={(event) =>
                                patchLadder({ minimumScore: Number(event.target.value) / 100 })
                              }
                            />
                            <b>{percent(draft.ladder.minimumScore)}</b>
                          </div>
                        </div>
                        <div className="journey-dial">
                          <span className="journey-diallab">
                            How far clear of the next best row
                          </span>
                          <div className="journey-dialset">
                            <input
                              type="range"
                              min={0}
                              max={20}
                              step={1}
                              aria-label="How far clear of the next best row"
                              value={Math.round(draft.ladder.runnerUpMargin * 100)}
                              onChange={(event) =>
                                patchLadder({ runnerUpMargin: Number(event.target.value) / 100 })
                              }
                            />
                            <b>{percent(draft.ladder.runnerUpMargin)}</b>
                          </div>
                        </div>
                      </div>
                      <p className="journey-dialnote">
                        {!rungCost('fuzzy') ? (
                          rungNote
                        ) : (rungCost('fuzzy')?.wrongRows ?? 0) > 0 ? (
                          <>
                            At these settings{' '}
                            <b>{rungCost('fuzzy')?.wrongRows} readings land on the wrong row</b>.
                            Raise either dial until that reads nought.
                          </>
                        ) : (
                          'At these settings nothing lands on the wrong row. Lowering either dial fills more and starts costing you that.'
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="journey-dialnote">
                      {(rungCost('character-confusion')?.wrongRows ?? 0) > 0 ? (
                        <>
                          Allowing for those,{' '}
                          <b>
                            {rungCost('character-confusion')?.wrongRows} readings land on the wrong
                            row
                          </b>
                          .
                        </>
                      ) : (
                        'Two rows surviving the same fold is a refusal, not a guess: nothing in the reading says which was meant.'
                      )}
                    </p>
                  )}
                </Rung>
              </div>
            </details>
          </div>
          <section className="journey-trial" aria-label="Try a reading">
            <label className="journey-trial-label" htmlFor="journey-probe">
              <FlaskConical size={16} aria-hidden="true" />
              Try a reading
            </label>
            <div className={`journey-probe-box ${markState}`}>
              <input
                id="journey-probe"
                value={probe}
                spellCheck={false}
                placeholder="Type a value from a document"
                onChange={(event) => {
                  setProbeTouched(true);
                  setProbe(event.target.value);
                }}
              />
              {markState && <ResultMark state={markState} />}
            </div>
            {readings.length > 0 && (
              <div className="journey-tries" role="group" aria-label="Recent readings">
                {[...new Set(readings)].slice(0, 5).map((reading, index) => (
                  <button
                    key={reading}
                    type="button"
                    className={reading === probe ? 'on' : ''}
                    onClick={() => {
                      setProbeTouched(true);
                      setProbe(reading);
                    }}
                  >
                    {reading}
                    {index === 0 && (
                      <>
                        {' '}
                        <small>latest</small>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div
              className={`journey-trial-result ${markState || (wireState === 'impossible' ? 'warn' : 'idle')}`}
              role="status"
              data-outcome={
                mismatch && !probeTouched
                  ? 'impossible'
                  : missing
                    ? 'incomplete'
                    : trialError
                      ? 'error'
                      : (shownVerdict?.match.outcome ?? 'waiting')
              }
            >
              {markState && <ResultMark state={markState} large />}
              {wireState === 'impossible' && <ResultMark state="tie" large />}
              <div className="journey-result-copy">
                {wireState === 'impossible' ? (
                  <>
                    <strong>Step 1 doesn't fit</strong>
                    <span>Type a reading to test the list anyway.</span>
                  </>
                ) : missing ? (
                  <>
                    <strong>Not ready to try</strong>
                    <span>{missing}</span>
                  </>
                ) : trialError ? (
                  <>
                    <strong>Couldn't try it</strong>
                    <span role="alert" data-telemetry="error:lookup-check-failed">
                      {trialError}
                    </span>
                    <button
                      type="button"
                      className="quiet-button small journey-retry"
                      onClick={() => {
                        setVerdict({ signature: trialSignature, status: 'pending' });
                        setTrialAttempt((attempt) => attempt + 1);
                      }}
                    >
                      <RotateCw size={14} />
                      Retry reading
                    </button>
                  </>
                ) : wireState === 'through' && shownVerdict ? (
                  <>
                    <strong>Row {matchedRowNumber?.toLocaleString()}</strong>
                    <span>{shownVerdict.match.explanation}</span>
                  </>
                ) : tie ? (
                  <>
                    <strong>
                      {tieCount > 1 ? `${tieCount} rows fit` : 'More than one row fits'}
                    </strong>
                    <span>It won't guess, so this goes to review</span>
                  </>
                ) : shownVerdict ? (
                  <>
                    <strong>No row</strong>
                    <span>{fixes?.headline ?? shownVerdict.match.explanation}</span>
                  </>
                ) : wireState === 'running' ? (
                  <strong>Trying…</strong>
                ) : (
                  <span>Type a reading to see which row it finds.</span>
                )}
              </div>
            </div>
            {fixes && (
              <div className="journey-fix" data-control={fixes.control}>
                <SlidersHorizontal size={16} aria-hidden="true" />
                <span>{fixes.action}</span>
                <button
                  type="button"
                  className="quiet-button small"
                  onClick={() => showControl(fixes.control)}
                >
                  Show me
                </button>
              </div>
            )}
            {tie && tiedRows.length > 0 && (
              <ul className="journey-tied" aria-label="Rows that fit">
                {tiedRows.map((row, index) => (
                  <li key={row.rowNumber}>
                    <span>row {row.rowNumber.toLocaleString()}</span>
                    <b>{row.matchedValue ?? row.returns?.[0]?.value ?? ''}</b>
                    <em>{tieAlike(index)}</em>
                  </li>
                ))}
              </ul>
            )}
            {(wireState === 'through' || (wireState === 'stopped' && !tie)) && (
              <div className="journey-fills">
                <span className="journey-pane-label">It fills</span>
                {draft.returns.map((entry, index) => {
                  const value = matchedValue(entry);
                  const before = probe.trim();
                  return (
                    <div className="journey-fill" key={index}>
                      <span className="journey-fill-field">
                        {entry.fieldName || 'No field chosen'}
                      </span>
                      {wireState !== 'through' ? (
                        <em>{onMissSay}</em>
                      ) : !value ? (
                        <em>blank in this row</em>
                      ) : listSpelling(entry) && before && before !== value ? (
                        <span
                          className="journey-fill-change"
                          aria-label={`${before} becomes ${value}`}
                        >
                          <span className="journey-was">{before}</span>
                          <ArrowRight size={14} aria-hidden="true" />
                          <b>{value}</b>
                        </span>
                      ) : (
                        <b>{value}</b>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {(wireState === 'through' ||
              wireState === 'running' ||
              (wireState === 'waiting' && !missing)) && (
              <p className="journey-else">
                {draft.onMiss === 'blank' ? (
                  <Eraser size={15} aria-hidden="true" />
                ) : draft.onMiss === 'keep' ? (
                  <FileText size={15} aria-hidden="true" />
                ) : (
                  <Inbox size={15} aria-hidden="true" />
                )}
                {draft.onMiss === 'keep'
                  ? 'If nothing matches, what was read is kept'
                  : `If nothing matches, it's ${onMissSay}`}
              </p>
            )}
            <section className="journey-measurement" aria-label="Recent reading measurements">
              <div className="journey-measure-head">
                <span className="journey-countedon" role="status">
                  {costError ? (
                    <>
                      {costError}{' '}
                      <button
                        className="quiet-button small"
                        onClick={() => {
                          setCost(null);
                          setCostAttempt((attempt) => attempt + 1);
                        }}
                      >
                        <RotateCw size={14} />
                        Retry measurement
                      </button>
                    </>
                  ) : measured?.measuredFrom === 'table-simulation' ? (
                    "Counted on the list's own values, misread the way a scanner misreads them."
                  ) : missing !== null || !sourceField ? (
                    'Last readings'
                  ) : measured && measured.readingCount > 0 ? (
                    `${sourceField.name}'s last ${plural(measured.readingCount, 'reading')}`
                  ) : (
                    `${sourceField.name}'s last readings`
                  )}
                </span>
                {measured?.measuredFrom === 'table-simulation' && !costError && (
                  <span className="journey-sim">Simulation</span>
                )}
              </div>
              {measured &&
                measured.measuredFrom === 'field-readings' &&
                measured.readingCount > 0 &&
                !costError && <MeasureBar report={measured} heldWord={heldWord} />}
              <div
                className="journey-tally"
                aria-label="What this costs across this field's last readings"
              >
                {measured &&
                measured.measuredFrom === 'field-readings' &&
                measured.readingCount > 0 &&
                !costError ? (
                  <>
                    <div className="good">
                      <b>{measured.filled}</b>
                      <small>filled</small>
                    </div>
                    <div className="held">
                      <b>{measured.held}</b>
                      <small>{heldWord}</small>
                    </div>
                    <div className={measured.wrongRows ? 'bad' : 'good'}>
                      <b>{measured.wrongRows}</b>
                      <small>wrong row</small>
                    </div>
                  </>
                ) : (
                  <p className="journey-tally-empty">
                    {costError
                      ? 'Measurement unavailable.'
                      : measured?.measuredFrom === 'table-simulation'
                        ? `${sourceField?.name ?? 'This field'} has nothing read yet.`
                        : measured
                          ? 'No field readings were available for this measurement.'
                          : missing !== null || !sourceField
                            ? 'Measured once the lookup is complete.'
                            : 'Counting recent readings…'}
                  </p>
                )}
              </div>
            </section>
          </section>
          <footer className="journey-foot">
            {error && (
              <p
                className="journey-error"
                role="alert"
                data-telemetry={
                  error === WAIT_TO_LEAVE ? 'error:lookup-busy' : 'error:lookup-save-failed'
                }
              >
                <TriangleAlert size={16} aria-hidden="true" />
                <span>{error}</span>
              </p>
            )}
            {lockReason ? (
              <p className="journey-lock">
                <Lock size={15} aria-hidden="true" />
                {lockReason}
              </p>
            ) : (
              hasUnsavedChanges && <p className="journey-dirty">Unsaved changes</p>
            )}
            <div className="journey-foot-actions">
              <button
                className="secondary-button"
                onClick={() => requestLeave(onClose)}
                disabled={busy}
              >
                Close
              </button>
              <button
                className="primary-button"
                disabled={
                  busy ||
                  !draft.name.trim() ||
                  missing !== null ||
                  mismatch !== null ||
                  ruleToSave === null
                }
                title={mismatch ? 'Fix step 1 to save' : (missing ?? undefined)}
                onClick={() => void save()}
              >
                {saving ? (
                  <>
                    <LoaderCircle className="spin" size={15} />
                    Saving…
                  </>
                ) : (
                  'Save lookup'
                )}
              </button>
            </div>
          </footer>
        </div>
      )}
      {pendingLeave && (
        <WorkspaceDialog
          telemetry="lookup-leave"
          title="Leave without saving?"
          compact
          onClose={() => setPendingLeave(null)}
        >
          <div className="journey-leave-prompt">
            <p>Your changes to this lookup have not been saved.</p>
            <div>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setPendingLeave(null)}
              >
                Keep editing
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  const leave = pendingLeave.leave;
                  if (savedDraft) setDraft(JSON.parse(savedDraft) as JourneyDraft);
                  setImportName('');
                  setImporting(false);
                  navigationState.current.dirty = false;
                  setPendingLeave(null);
                  leave();
                }}
              >
                Discard changes
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
    </div>
  );
}

/** Aa Words or # Codes: what kind of value a column (or a field's readings) holds. */
function KindChip({ kind }: { kind: string }) {
  const code = normalizeValueKind(kind) === VALUE_KIND_CODE;
  return (
    <span className="journey-kind">
      <b aria-hidden="true">{code ? '#' : 'Aa'}</b>
      {code ? 'Codes' : 'Words'}
    </span>
  );
}

/** The mark a reading earns: ✓ one row, ✕ none, ! more than one (or it could not be tried), and a spinner while it runs. */
function ResultMark({ state, large = false }: { state: string; large?: boolean }) {
  const label =
    state === 'ok'
      ? 'Found a row'
      : state === 'no'
        ? 'No row'
        : state === 'tie'
          ? 'More than one row'
          : state === 'err'
            ? 'Could not try it'
            : 'Trying';
  return (
    <span className={`journey-mark ${state}${large ? ' large' : ''}`} role="img" aria-label={label}>
      {state === 'busy' ? (
        <LoaderCircle className="spin" size={large ? 20 : 16} aria-hidden="true" />
      ) : state === 'ok' ? (
        <Check size={large ? 21 : 16} aria-hidden="true" />
      ) : state === 'no' ? (
        <X size={large ? 21 : 16} aria-hidden="true" />
      ) : (
        <b aria-hidden="true">!</b>
      )}
    </span>
  );
}

/**
 * The field's last readings as one mark each: filled, not filled, and filled from
 * the wrong row. Drawn from the engine's counts and nothing else — a reading is a
 * segment only because the report counted it.
 */
function MeasureBar({ report, heldWord }: { report: LookupCostReport; heldWord: string }) {
  const right = Math.max(0, report.filled - report.wrongRows);
  const segments = [
    ...Array.from({ length: right }, () => 'f'),
    ...Array.from({ length: Math.max(0, report.wrongRows) }, () => 'w'),
    ...Array.from({ length: Math.max(0, report.held) }, () => 'h'),
  ].slice(0, Math.max(report.readingCount, 0));
  return (
    <span
      className="journey-bar"
      role="img"
      aria-label={`${report.filled} filled, ${report.wrongRows} of them from the wrong row, ${report.held} ${heldWord}, out of ${report.readingCount}`}
    >
      {segments.map((kind, index) => (
        <i key={index} className={kind} />
      ))}
    </span>
  );
}

function Rung({
  id,
  label,
  detail,
  cost,
  note,
  on,
  fixed,
  costly,
  disabled,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  detail: string;
  /** The measurement, which IS the label: there is no Measure button to press. */
  cost: LookupRungCost | null;
  /** What stands in for the measurement while there is not one, in as many words. */
  note: string;
  on: boolean;
  fixed?: boolean;
  costly?: boolean;
  disabled?: boolean;
  onToggle?: (next: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className={`journey-rung ${on ? 'on' : ''} ${costly ? 'costly' : ''}`} data-rung={id}>
      <label className="journey-rungtop">
        <input
          type="checkbox"
          checked={on}
          disabled={fixed || disabled}
          onChange={(event) => onToggle?.(event.target.checked)}
        />
        <span>
          <b>
            {label}
            {fixed ? ' - always' : ''}
          </b>
          <small>{detail}</small>
          <small className={`journey-cost ${cost ? (cost.wrongRows > 0 ? 'bad' : 'good') : ''}`}>
            {/* Counted, or measured and found to have nothing to count. Those are
                different sentences: a rung stuck on "counting…" forever reads as a
                screen that has hung. */}
            {cost ? cost.sentence : note}
          </small>
        </span>
      </label>
      {on && (
        <div className="journey-rungbody" onMouseDown={(event) => event.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Why a reading found no row, and the one control that would change it.
 *
 * "No match" alone sends an operator hunting through a file they cannot see. Every
 * branch below names a control that is on this screen, because a diagnosis whose
 * remedy is somewhere else is a diagnosis nobody can act on.
 *
 * A tie is not diagnosed here. More than one row fitting is the lookup refusing to
 * guess, which is the right answer, so there is no setting to suggest for it.
 */
function namedFix(input: {
  draft: JourneyDraft;
  kind: string;
  verdict: LookupMatch;
  probe: string;
  columns: string[];
  rows: string[][];
  /** How many rows the list has, of which `rows` is the sample read here. */
  totalRowCount: number;
  rule: Parameters<typeof diagnoseNoMatch>[0]['rule'];
}): { headline: string; control: string; action: ReactNode } | null {
  const { draft, kind, verdict, probe, columns, rows, totalRowCount, rule } = input;
  if (verdict.outcome !== 'unmatched') return null;
  const forgivingName =
    kind === VALUE_KIND_CODE ? 'Allowing for glyphs Cortexa confuses' : 'Close enough to be a typo';

  // The rows already read are searched the way the diagnosis searches them, so the
  // nearest row and where the value really is come from the file rather than from
  // the one sentence a refusal carries.
  const findings = diagnoseNoMatch({
    value: probe,
    rule,
    columnNames: columns,
    rows,
    scannedRowCount: rows.length,
    totalRowCount: Math.max(totalRowCount, rows.length),
    findProfile: null,
  });
  const nearest = findings.find((finding) => /closest value/i.test(finding.text));
  const lead = findings.find((finding) => finding.severity === 'lead');
  // Promised only when the diagnosis ran the rung's own guards and found it would.
  const wouldCatch = findings.some((finding) => /would match this reading/i.test(finding.text));
  const headline = nearest?.text ?? lead?.text ?? 'Nothing in this column resembles that reading.';

  // The headline names a turned-off spelling switch that would match the reading (owner item
  // 110(b)): the advice is that switch, not a looser rung.
  const strictSwitch = STRICT_SWITCH[kind];
  if (strictSwitch && (nearest ?? lead)?.switchId === strictSwitch.id) {
    return {
      headline,
      control: strictSwitch.id,
      action: (
        <>
          Turn on <b>{strictSwitch.label}</b>.
        </>
      ),
    };
  }
  if (!draft.ladder.forgiving) {
    return {
      headline,
      control: 'forgiving',
      action: (
        <>
          Turn on <b>{forgivingName}</b>
          {wouldCatch ? ' to catch this.' : '.'}
        </>
      ),
    };
  }
  if (kind === VALUE_KIND_NAME && verdict.score > 0 && verdict.score < draft.ladder.minimumScore) {
    return {
      headline,
      control: 'minimum-score',
      action: (
        <>
          They are {percent(verdict.score)} alike. <b>How alike it has to be</b> is set to{' '}
          {percent(draft.ladder.minimumScore)}.
        </>
      ),
    };
  }
  if (
    verdict.runnerUpScore > 0 &&
    verdict.score - verdict.runnerUpScore < draft.ladder.runnerUpMargin
  ) {
    return {
      headline,
      control: 'runner-up-margin',
      action: (
        <>
          The next best row is {percent(verdict.runnerUpScore)} alike.{' '}
          <b>How far clear of the next best row</b> is set to {percent(draft.ladder.runnerUpMargin)}
          .
        </>
      ),
    };
  }
  return {
    headline,
    control: 'find-column',
    action: (
      <>
        Check the column it is <b>matched against</b>.
      </>
    ),
  };
}

function EmptyJourney({
  importing,
  busy,
  name,
  error,
  onName,
  onBegin,
  onChoose,
}: {
  importing: boolean;
  busy: boolean;
  name: string;
  error: string;
  onName: (value: string) => void;
  onBegin: () => void;
  onChoose: () => void;
}) {
  return (
    <div className="journey-empty">
      <span className="journey-empty-icon">
        <FileSpreadsheet size={22} aria-hidden="true" />
      </span>
      <strong>Add a list for this job</strong>
      <p>
        Import a vendor list, chart of accounts, or another table. It stays with this job
        {/* PAIGE: a Paige list is kept with the job, not on a computer (`local-storage`). */}
        {hostHas('local-storage') ? ' on this computer.' : '.'}
      </p>
      {importing ? (
        <div className="journey-import">
          <input
            autoFocus
            value={name}
            placeholder="e.g. Vendor list"
            aria-label="New list name"
            onChange={(event) => onName(event.target.value)}
          />
          <button
            className="primary-button small"
            disabled={busy || !name.trim()}
            onClick={onChoose}
          >
            {busy ? <LoaderCircle className="spin" size={14} /> : <Upload size={14} />} Choose the
            file…
          </button>
        </div>
      ) : (
        <button className="primary-button" onClick={onBegin}>
          <Upload size={16} /> Import a list…
        </button>
      )}
      {/* A <div>: the message can carry a Technical details disclosure, which a <p> may not hold. */}
      {error && (
        <div className="journey-warn" role="alert" data-telemetry="error:lookup-import-failed">
          <TriangleAlert size={16} aria-hidden="true" />
          <OperatorErrorText message={error} />
        </div>
      )}
    </div>
  );
}
